/**
 * OPS_WORKSPACE_DETAIL: 個別 workspace ドリルダウン (Phase 4 / issue #200)。
 * sales_counter 単体前提へ縮退（#1479、カウンターシリーズ4製品構想は2026-07-02撤退）。
 *
 * 設計: spec/ai-inbox-core/admin-ops/requirements.md §4.4 / wireframes.md §5
 *
 * 4 ペイン構成（OPS-DR-1〜4）:
 *   - 基本情報 (BasicInfoPane)
 *   - 利用量パネル (UsagePane)
 *   - 最近のアクティビティ (ActivityPane)
 *   - 課金履歴 (BillingPane)
 *
 * 守るライン:
 *   - PII を表示しない（API 側でマスキング済 + フロント側で raw 表示しない）
 *   - 個別 Lead は表示しない（OPS-DR-5 は Phase 2 拡張）
 *   - Stripe Customer Portal は外部リンクのみ（書き込み API は持たない）
 *
 * PC 専用 (min-width: 1024px、wireframes.md §Open Q3 確定)。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  deleteWorkspaceFeatureFlag,
  FeatureFlagApiError,
  type FeatureFlagListItem,
  type FeatureFlagsResult,
  fetchWorkspaceFeatureFlags,
  fetchWorkspaceSummary,
  type MaskedAuditLogRow,
  putWorkspaceFeatureFlag,
  type UsageWindow,
  type WorkspaceBasicInfo,
  type WorkspaceSummaryResult,
} from '../../lib/workspace-detail-api';
import {
  BILLING_STATUS_LABEL,
  formatRelative,
  PRODUCT_EMAIL_CONSENT_BADGE,
  PRODUCT_EMAIL_CONSENT_LABEL,
  PRODUCT_LABEL_FULL,
} from './labels';

export interface WorkspaceDetailPageProps {
  apiBaseUrl: string;
}

export function WorkspaceDetailPage({ apiBaseUrl }: WorkspaceDetailPageProps) {
  const params = useParams<{ id: string }>();
  const workspaceId = params.id ?? '';

  const summaryQuery = useQuery({
    queryKey: ['admin-ops-workspace-summary', workspaceId],
    queryFn: () => fetchWorkspaceSummary(apiBaseUrl, workspaceId),
    enabled: workspaceId !== '',
  });

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="hidden lg:block">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <Breadcrumb workspaceId={workspaceId} displayName={getDisplayName(summaryQuery.data)} />

          {summaryQuery.isLoading && <p className="mt-6 text-sm text-slate-500">読み込み中…</p>}
          {summaryQuery.isError && (
            <p className="mt-6 text-sm text-rose-600">
              API 取得に失敗しました: {(summaryQuery.error as Error).message}
            </p>
          )}

          {summaryQuery.data &&
            'configured' in summaryQuery.data &&
            !summaryQuery.data.configured && <UnconfiguredBanner />}

          {summaryQuery.data &&
            'error' in summaryQuery.data &&
            summaryQuery.data.error === 'workspace_not_found' && (
              <NotFoundBanner workspaceId={workspaceId} />
            )}

          {summaryQuery.data && 'basic' in summaryQuery.data && (
            <div className="mt-6 space-y-6">
              <BasicInfoPane basic={summaryQuery.data.basic} />
              <FeatureFlagsPane apiBaseUrl={apiBaseUrl} workspaceId={workspaceId} />
              <UsagePane summary={summaryQuery.data} />
              <ActivityPane activity={summaryQuery.data.activity} />
              <BillingPane summary={summaryQuery.data} />
            </div>
          )}
        </div>
      </div>

      <div className="block lg:hidden">
        <div className="mx-auto max-w-md px-6 py-12 text-center">
          <h1 className="text-lg font-semibold text-slate-900">PC 専用画面</h1>
          <p className="mt-3 text-sm text-slate-600">
            admin-ops は最小 1024px の画面幅でのみ動作します。
          </p>
        </div>
      </div>
    </main>
  );
}

function getDisplayName(data: ReturnType<typeof useQuery>['data'] | undefined): string {
  if (!data || typeof data !== 'object') return '';
  if ('basic' in data) {
    const b = (data as WorkspaceSummaryResult).basic;
    return b?.displayName ?? '';
  }
  return '';
}

function Breadcrumb({ workspaceId, displayName }: { workspaceId: string; displayName: string }) {
  return (
    <header>
      <p className="text-xs uppercase tracking-wider text-slate-500">ALYS Internal / admin-ops</p>
      <nav className="mt-1 flex items-center gap-2 text-sm text-slate-500">
        <Link to="/workspaces" className="hover:text-slate-700">
          workspace 一覧
        </Link>
        <span>/</span>
        <span className="text-slate-900">{displayName || workspaceId}</span>
      </nav>
      <h1 className="mt-1 text-2xl font-semibold text-slate-900">workspace 詳細</h1>
      <p className="mt-2 text-sm text-slate-600">
        OPS-DR-1〜4: 個人 PII は表示されません（集約値 + マスキング済 audit log）。
      </p>
    </header>
  );
}

function UnconfiguredBanner() {
  return (
    <section
      data-testid="unconfigured-banner"
      className="mt-6 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
    >
      <code className="mr-1 rounded bg-amber-100 px-1 text-xs">SALESCOUNTER_READONLY_DB_URL</code>（
      <code className="mx-1 rounded bg-amber-100 px-1 text-xs">alys_ops_readonly</code>
      の接続 URL）が admin-ops-api の Worker secret に設定されていません。手順は founder-setup.md §D
      を参照してください。
    </section>
  );
}

function NotFoundBanner({ workspaceId }: { workspaceId: string }) {
  return (
    <section className="mt-6 rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800">
      workspace が見つかりません: <code>{workspaceId}</code>
    </section>
  );
}

// ===================================================================
// OPS-DR-1: 基本情報
// ===================================================================

function BasicInfoPane({ basic }: { basic: WorkspaceBasicInfo }) {
  return (
    <PaneCard title="基本情報" subtitle="OPS-DR-1">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <DefRow label="製品">{PRODUCT_LABEL_FULL}</DefRow>
        <DefRow label="workspace 表示名">{basic.displayName}</DefRow>
        <DefRow label="slug">
          <code className="text-xs text-slate-500">{basic.slug}</code>
        </DefRow>
        <DefRow label="作成日">{formatRelative(basic.createdAt)}</DefRow>
        <DefRow label="プラン">{basic.plan ?? '—'}</DefRow>
        <DefRow label="課金状態">
          <BillingBadge status={basic.billingStatus} />
        </DefRow>
        <DefRow label="Stripe Customer">
          {basic.stripeCustomerIdMasked ? (
            <code className="text-xs">{basic.stripeCustomerIdMasked}</code>
          ) : (
            '—'
          )}
        </DefRow>
        <DefRow label="オンボーディング進捗">
          {`${Math.round(basic.onboardingProgress * 100)}%`}
        </DefRow>
        {/* #1886: Owner の製品お知らせメールの許可（Owner の誰かが許可なら案内メールが届く） */}
        <DefRow label="製品のお知らせメール（Owner）">
          <span
            className={`rounded px-2 py-0.5 text-xs ${PRODUCT_EMAIL_CONSENT_BADGE[basic.ownerProductEmailConsent]}`}
          >
            {PRODUCT_EMAIL_CONSENT_LABEL[basic.ownerProductEmailConsent]}
          </span>
          {basic.ownerProductEmailConsentChangedAt ? (
            <span className="ml-2 text-xs text-slate-500">
              {formatRelative(basic.ownerProductEmailConsentChangedAt)}に変更
            </span>
          ) : null}
        </DefRow>
      </dl>

      <h3 className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
        接続状態
      </h3>
      <ul className="mt-2 grid grid-cols-2 gap-2 text-sm">
        <ConnRow label="AI 自動応答" enabled={basic.connections.aiEnabled} />
        <ConnRow label="メール受信" enabled={basic.connections.emailConnected} />
        {basic.connections.shopifyConnected !== null && (
          <ConnRow label="Shopify" enabled={basic.connections.shopifyConnected} />
        )}
        {basic.connections.shopifyCustomAppState !== null && (
          <li className="text-slate-700">
            Custom App 状態:{' '}
            <span className="font-medium">{basic.connections.shopifyCustomAppState}</span>
          </li>
        )}
        {basic.connections.lineConnected !== null && (
          <ConnRow label="LINE" enabled={basic.connections.lineConnected} />
        )}
        {basic.connections.mapboxConnected !== null && (
          <ConnRow label="MapBox 地図 API" enabled={basic.connections.mapboxConnected} />
        )}
        {basic.connections.inquiryFormPublished !== null && (
          <ConnRow label="反響フォーム公開" enabled={basic.connections.inquiryFormPublished} />
        )}
      </ul>
    </PaneCard>
  );
}

// ===================================================================
// OPS-DR-2: 利用量
// ===================================================================

function UsagePane({ summary }: { summary: WorkspaceSummaryResult }) {
  const windows = summary.usage.windows;

  return (
    <PaneCard title="利用量" subtitle="OPS-DR-2">
      <table className="min-w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="py-2 pr-4 text-left">期間</th>
            <th className="py-2 pr-4 text-left">受信件数</th>
            <th className="py-2 pr-4 text-left">AI 結果数</th>
            <th className="py-2 pr-4 text-left">AI 採用率</th>
            <th className="py-2 pr-4 text-left">アクティブ user</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {windows.map((w) => (
            <UsageRow key={w.windowDays} window={w} />
          ))}
        </tbody>
      </table>
    </PaneCard>
  );
}

function UsageRow({ window: w }: { window: UsageWindow }) {
  return (
    <tr>
      <td className="py-2 pr-4 text-slate-700">直近 {w.windowDays}d</td>
      <td className="py-2 pr-4 text-slate-900">{w.leadsCount}</td>
      <td className="py-2 pr-4 text-slate-900">{w.aiResultsCount}</td>
      <td className="py-2 pr-4 text-slate-900">
        {w.aiAdoptionRate === null ? '—' : `${Math.round(w.aiAdoptionRate * 100)}%`}
      </td>
      <td className="py-2 pr-4 text-slate-900">{w.activeUsersCount}</td>
    </tr>
  );
}

// ===================================================================
// OPS-DR-3: 最近のアクティビティ
// ===================================================================

function ActivityPane({ activity }: { activity: WorkspaceSummaryResult['activity'] }) {
  return (
    <PaneCard title="最近のアクティビティ" subtitle="OPS-DR-3 / PII マスキング済">
      <p className="text-xs text-slate-500">直近 {activity.fetched} 件（最大 50）</p>
      {activity.rows.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">アクティビティがありません。</p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {activity.rows.map((row) => (
            <ActivityRow key={row.id} row={row} />
          ))}
        </ul>
      )}
    </PaneCard>
  );
}

function ActivityRow({ row }: { row: MaskedAuditLogRow }) {
  return (
    <li className="py-2 text-sm" data-testid="activity-row">
      <div className="flex items-center justify-between">
        <span className="font-medium text-slate-900">{row.actionName}</span>
        <time className="text-xs text-slate-500">{formatRelative(row.occurredAt)}</time>
      </div>
      <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-600">
        <span className="rounded bg-slate-100 px-1.5 py-0.5">{row.actorType}</span>
        <span className="text-slate-500">target: {row.targetType}</span>
        {row.reason && <span className="text-slate-500">理由: {row.reason}</span>}
      </div>
    </li>
  );
}

// ===================================================================
// OPS-DR-4: 課金履歴
// ===================================================================

function BillingPane({ summary }: { summary: WorkspaceSummaryResult }) {
  const b = summary.billing;
  return (
    <PaneCard title="課金履歴" subtitle="OPS-DR-4 / Stripe 書き込み無し（外部リンクのみ）">
      <div data-testid="billing-pane">
        <p className="text-sm text-slate-700">
          現在の状態: <BillingBadge status={b.currentStatus} />
        </p>
        {b.timeline.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">状態遷移履歴がありません。</p>
        ) : (
          <ul className="mt-3 space-y-2 text-sm">
            {b.timeline.map((e) => (
              <li
                key={`${e.status}-${e.enteredAt}-${e.endedAt ?? 'open'}`}
                className="flex items-center gap-3"
              >
                <BillingBadge status={e.status} />
                <span className="text-slate-700">
                  {formatRelative(e.enteredAt)} 〜{' '}
                  {e.endedAt ? formatRelative(e.endedAt) : '継続中'}
                </span>
                {/* #1863: Stripe の監査ログが無い期間は subscriptions の日時からの概算。 */}
                {e.source === 'approximate' && (
                  <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
                    概算
                  </span>
                )}
                {e.reason && <span className="text-xs text-slate-500">({e.reason})</span>}
              </li>
            ))}
          </ul>
        )}
        {/* #1863: どこから実際の遷移かを示す（それ以前は概算）。履歴が無いときは出さない。 */}
        {b.timeline.length > 0 && (
          <p data-testid="billing-accurate-since" className="mt-2 text-xs text-slate-500">
            {b.accurateSince
              ? `${new Date(b.accurateSince).toLocaleDateString('ja-JP')} 以降は Stripe の記録で確認した状態です（それ以前は概算）。`
              : 'Stripe の記録がまだ無いため、すべて概算です（記録は次の課金イベントから増えます）。'}
          </p>
        )}
        {b.stripeCustomerPortalUrl && (
          <p className="mt-4 text-sm">
            <a
              href={b.stripeCustomerPortalUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="stripe-portal-link"
              className="text-sky-700 underline hover:text-sky-800"
            >
              Stripe Customer Portal を新タブで開く ↗
            </a>
          </p>
        )}
      </div>
    </PaneCard>
  );
}

// ===================================================================
// OPS-DR-9: 機能フラグ（#1300・軸A = alys_owner の運用トグル）
//
// このワークスペースの機能を運用上 開放 / 閉鎖する制御面。
// registry 由来・当該テナントの product に関係するフラグのみ表示。
// 各行 = ラベル / 説明 / 有効状態（既定 vs 上書きの別）/ トグル。
//
// 守るライン #2（課金凍結）: 課金 / プラン / 価格 / アップグレード文言を
//   どの状態（トグル・説明・確認・トースト・空状態）にも出さない。
//   開放/閉鎖は運用上の段階開放（rollout 制御 + 停止弁）であって paywall ではない。
//   BillingPane（OPS-DR-4）とは参照も連動もしない。
//
// 設計: spec/sales-counter/screen-contracts/admin-ops-workspace-feature-flags.md
//   / state-matrices/admin-ops-workspace-feature-flags.md
//
// これは admin-ops 初の **書き込みアクション**（既存ペインは read-only）。
// トグルは保存 API + 監査記録を伴い、保存中は当該行を disabled にする。
// ===================================================================

interface FlagFeedback {
  tone: 'success' | 'error' | 'forbidden';
  message: string;
}

interface FlagConfirm {
  key: string;
  label: string;
  /** 目標状態（true = 開放 / false = 閉鎖）。 */
  to: boolean;
}

export function FeatureFlagsPane({
  apiBaseUrl,
  workspaceId,
}: {
  apiBaseUrl: string;
  workspaceId: string;
}) {
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<FlagConfirm | null>(null);
  const [feedback, setFeedback] = useState<FlagFeedback | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const flagsQuery = useQuery({
    queryKey: ['admin-ops-workspace-flags', workspaceId],
    queryFn: () => fetchWorkspaceFeatureFlags(apiBaseUrl, workspaceId),
    enabled: workspaceId !== '',
    retry: false,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['admin-ops-workspace-flags', workspaceId] });

  const onMutationError = (err: unknown) => {
    if (err instanceof FeatureFlagApiError && err.status === 403) {
      // X7 ToggleForbidden / L5 相当: 状態を変えず「権限がありません」。
      setFeedback({ tone: 'forbidden', message: '権限がありません。' });
      return;
    }
    // X6 ToggleError: 楽観更新していないため見かけ上の開放/閉鎖は残らない（= revert 済み）。
    setFeedback({
      tone: 'error',
      message: '保存に失敗しました。もう一度お試しください。',
    });
  };

  const toggleMutation = useMutation({
    mutationFn: (v: { key: string; label: string; enabled: boolean }) =>
      putWorkspaceFeatureFlag(apiBaseUrl, workspaceId, v.key, v.enabled),
    onMutate: (v) => {
      setPendingKey(v.key);
      setFeedback(null);
    },
    onSuccess: (_res, v) => {
      // X4 / X5: 成功トースト（課金示唆なし）。監査は API 側で記録済。
      setFeedback({
        tone: 'success',
        message: v.enabled ? `${v.label}を開放しました。` : `${v.label}を閉じました。`,
      });
      invalidate();
    },
    onError: onMutationError,
    onSettled: () => setPendingKey(null),
  });

  const resetMutation = useMutation({
    mutationFn: (v: { key: string; label: string }) =>
      deleteWorkspaceFeatureFlag(apiBaseUrl, workspaceId, v.key),
    onMutate: (v) => {
      setPendingKey(v.key);
      setFeedback(null);
    },
    onSuccess: (_res, v) => {
      setFeedback({ tone: 'success', message: `${v.label}を既定に戻しました。` });
      invalidate();
    },
    onError: onMutationError,
    onSettled: () => setPendingKey(null),
  });

  const isMutating = toggleMutation.isPending || resetMutation.isPending;

  return (
    <PaneCard
      title="機能フラグ"
      subtitle="OPS-DR-9 / このワークスペースの機能を運用上 開放・閉鎖します（rollout 制御 / 停止弁）"
    >
      {feedback && <FlagFeedbackBanner feedback={feedback} onDismiss={() => setFeedback(null)} />}

      <FeatureFlagsBody
        flagsQuery={flagsQuery}
        pendingKey={pendingKey}
        isMutating={isMutating}
        onRequestToggle={(flag) =>
          setConfirm({ key: flag.key, label: flag.label, to: !flag.effective })
        }
        onReset={(flag) => resetMutation.mutate({ key: flag.key, label: flag.label })}
      />

      {confirm && (
        <FlagConfirmDialog
          confirm={confirm}
          pending={toggleMutation.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            const c = confirm;
            setConfirm(null);
            toggleMutation.mutate({ key: c.key, label: c.label, enabled: c.to });
          }}
        />
      )}
    </PaneCard>
  );
}

function FeatureFlagsBody({
  flagsQuery,
  pendingKey,
  isMutating,
  onRequestToggle,
  onReset,
}: {
  flagsQuery: ReturnType<typeof useQuery<Awaited<ReturnType<typeof fetchWorkspaceFeatureFlags>>>>;
  pendingKey: string | null;
  isMutating: boolean;
  onRequestToggle: (flag: FeatureFlagListItem) => void;
  onReset: (flag: FeatureFlagListItem) => void;
}) {
  // L1 Loading: 状態確定前はトグルを出さない。
  if (flagsQuery.isLoading) {
    return <p className="text-sm text-slate-500">読み込み中…</p>;
  }
  // L4 LoadError / L5 Forbidden(GET が 403): 取得できないときはトグルを出さない。
  if (flagsQuery.isError) {
    const err = flagsQuery.error;
    const forbidden = err instanceof FeatureFlagApiError && err.status === 403;
    return (
      <div>
        <p className="text-sm text-rose-600">
          {forbidden ? '権限がありません。' : `取得に失敗しました: ${(err as Error).message}`}
        </p>
        {!forbidden && (
          <button
            type="button"
            onClick={() => flagsQuery.refetch()}
            className="mt-2 rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
          >
            再試行
          </button>
        )}
      </div>
    );
  }

  const data = flagsQuery.data;
  if (!data) return null;

  // L6 Unconfigured: サーバ側の読み取り接続・書込接続がどちらも未配線（Founder 手作業待ち）。
  //   壊れ・空とは別物。読み取り接続だけあれば L7 ReadOnly になる（#1871）。
  if (data.configured === false) {
    return (
      <p className="text-sm text-slate-500" data-testid="flags-unconfigured">
        機能フラグは未配線です。Founder 手作業でサーバ側の読み取り接続を設定すると表示され、
        書込接続も設定すると切り替えられます。
      </p>
    );
  }
  if ('error' in data && data.error === 'workspace_not_found') {
    return <p className="text-sm text-rose-600">workspace が見つかりません。</p>;
  }

  const { flags, writable } = data as FeatureFlagsResult;
  // L3 EmptyNoApplicableFlags: 当該 product に関係するフラグが無い（壊れ・未配線と区別）。
  if (flags.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        このワークスペースの製品で開放対象の機能はありません。
      </p>
    );
  }

  // L2 Loaded: 各行（F 群）を並べる。L7 ReadOnly（writable:false・#1871）は状態だけ見せ、
  //   トグルと「既定に戻す」を出さない。
  return (
    <>
      {!writable && (
        <p
          className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
          data-testid="flags-readonly"
        >
          閲覧のみ。切り替えるには書込用の接続が必要です。
        </p>
      )}
      <ul className="divide-y divide-slate-100">
        {flags.map((flag) => (
          <FlagRow
            key={flag.key}
            flag={flag}
            readOnly={!writable}
            saving={pendingKey === flag.key && isMutating}
            rowDisabled={isMutating}
            onRequestToggle={() => onRequestToggle(flag)}
            onReset={() => onReset(flag)}
          />
        ))}
      </ul>
      {writable && (
        <p className="mt-4 text-xs text-slate-500">
          開放・閉鎖の操作は監査ログに記録されます（誰が・いつ・どのフラグを・どちらへ）。
        </p>
      )}
    </>
  );
}

function FlagRow({
  flag,
  readOnly,
  saving,
  rowDisabled,
  onRequestToggle,
  onReset,
}: {
  flag: FeatureFlagListItem;
  readOnly: boolean;
  saving: boolean;
  rowDisabled: boolean;
  onRequestToggle: () => void;
  onReset: () => void;
}) {
  return (
    <li
      className="flex items-start justify-between gap-4 py-3"
      data-testid={`flag-row-${flag.key}`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-900">{flag.label}</span>
          <code className="text-xs text-slate-400">{flag.key}</code>
          <FlagStatusBadge flag={flag} />
        </div>
        <p className="mt-1 text-sm text-slate-600">{flag.description}</p>
        {flag.overridden && (
          <p className="mt-1 text-xs text-slate-500" data-testid={`flag-meta-${flag.key}`}>
            理由: {flag.reason ?? '—'} ・ 更新者: {flag.updatedBy ?? '—'} ・ 更新:{' '}
            {formatRelative(flag.updatedAt)}
          </p>
        )}
        {flag.overridden && !readOnly && (
          <button
            type="button"
            disabled={rowDisabled}
            onClick={onReset}
            className="mt-1 text-xs text-slate-500 underline hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            既定に戻す
          </button>
        )}
      </div>
      {!readOnly && (
        <div className="flex shrink-0 items-center gap-2">
          {saving && <span className="text-xs text-slate-500">保存中…</span>}
          <FlagToggle
            flagKey={flag.key}
            label={flag.label}
            checked={flag.effective}
            disabled={saving || rowDisabled}
            onToggle={onRequestToggle}
          />
        </div>
      )}
    </li>
  );
}

function FlagStatusBadge({ flag }: { flag: FeatureFlagListItem }) {
  // F2 OverrideOn / F3 OverrideOff / F1 DefaultOff の読み分け（バッジで区別）。
  if (flag.overridden && flag.overrideEnabled === true) {
    return (
      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">
        開放中（上書き）
      </span>
    );
  }
  if (flag.overridden && flag.overrideEnabled === false) {
    return (
      <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
        閉鎖（上書き）
      </span>
    );
  }
  return (
    <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
      {flag.effective ? '既定（オン）' : '既定（オフ）'}
    </span>
  );
}

function FlagToggle({
  flagKey,
  label,
  checked,
  disabled,
  onToggle,
}: {
  flagKey: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={`${label}を${checked ? '閉じる' : '開放する'}`}
      data-testid={`flag-toggle-${flagKey}`}
      disabled={disabled}
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-emerald-500' : 'bg-slate-300'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function FlagFeedbackBanner({
  feedback,
  onDismiss,
}: {
  feedback: FlagFeedback;
  onDismiss: () => void;
}) {
  const isSuccess = feedback.tone === 'success';
  const cls = isSuccess
    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
    : 'border-rose-300 bg-rose-50 text-rose-800';
  return (
    <div
      data-testid="flag-feedback"
      className={`mb-3 flex items-center justify-between rounded-md border px-3 py-2 text-sm ${cls}`}
    >
      <span>{feedback.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="閉じる"
        className="ml-3 text-xs underline"
      >
        閉じる
      </button>
    </div>
  );
}

function FlagConfirmDialog({
  confirm,
  pending,
  onConfirm,
  onCancel,
}: {
  confirm: FlagConfirm;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const opening = confirm.to === true;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={opening ? '機能フラグの開放確認' : '機能フラグの閉鎖確認'}
      className="fixed inset-0 z-20 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">
          {opening ? `${confirm.label}を開放しますか？` : `${confirm.label}を閉じますか？`}
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          {opening
            ? 'このワークスペースで開放します。テナントの導線に表示されます。'
            : '閉じます。テナントの導線から消えます。後で再開放できます。'}
        </p>
        <p className="mt-2 text-xs text-slate-500">この操作は監査ログに記録されます。</p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className={`rounded px-3 py-1.5 text-sm text-white disabled:opacity-50 ${
              opening ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-700 hover:bg-slate-800'
            }`}
          >
            {opening ? '開放する' : '閉じる'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// 汎用パーツ
// ===================================================================

function PaneCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
      <header className="mb-3">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

function DefRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-xs uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="text-slate-900">{children}</dd>
    </>
  );
}

function ConnRow({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <li className="flex items-center justify-between rounded bg-slate-50 px-2 py-1 text-slate-700">
      <span>{label}</span>
      {enabled ? (
        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">接続</span>
      ) : (
        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">未接続</span>
      )}
    </li>
  );
}

const BILLING_BADGE_CLASS: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  trialing: 'bg-sky-100 text-sky-800',
  past_due: 'bg-rose-100 text-rose-800',
  canceled: 'bg-slate-100 text-slate-600',
};

function BillingBadge({ status }: { status: WorkspaceBasicInfo['billingStatus'] }) {
  return (
    <span
      className={`rounded px-2 py-0.5 text-xs ${BILLING_BADGE_CLASS[status] ?? 'bg-slate-100 text-slate-600'}`}
    >
      {BILLING_STATUS_LABEL[status]}
    </span>
  );
}
